/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IMaskInput } from "react-imask";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Form, FieldGroup, Select, Switch } from "core/components";
import { toTitleCase } from "core/helpers";
import { navigateTo } from "core/navigation";
import { ItemSubcategoryFormData, ItemSubcategorySchema } from "core/schema";
import { admin_item_subcategories_path, admin_item_subcategory_path } from "routes";

type ItemSubcategoryFormProps = {
  item_subcategory: ItemSubcategoryFormData;
  item_categories?: Array<Record<string, unknown>>;
  item_type: string;
};

const CostTypeMask = "COS00000";
const InventoryTypeMask = "00000000";

const ItemSubcategoryForm = ({ item_subcategory, item_categories, item_type }: ItemSubcategoryFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<ItemSubcategoryFormData>({
    mode: "onChange",
    defaultValues: {
      ...ItemSubcategorySchema.cast(item_subcategory),
      item_type
    },
    resolver: yupResolver(ItemSubcategorySchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const codeProps =
    item_type == "cost"
      ? {
          mask: CostTypeMask,
          maxLength: 8,
          placeholder: "COS100011"
        }
      : {
          mask: InventoryTypeMask,
          maxLength: 7,
          placeholder: "1010001"
        };
  const itemCategoryProps =
    item_type == "cost"
      ? {
          label: "Cost Type",
          placeholder: "Select Cost Type"
        }
      : {
          label: "Inventory Type",
          placeholder: "Select Inventory Type"
        };

  const save = async (data: ItemSubcategoryFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_item_subcategory_path(item_subcategory), data);
      } else {
        await api.post(admin_item_subcategories_path(item_subcategory), data);
      }

      navigateTo(admin_item_subcategories_path({ item_type: item_type }));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={item_subcategory.id ? `Edit ${toTitleCase(item_type)} Subtype` : `New ${toTitleCase(item_type)} Subtype`}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_item_subcategories_path({ item_type: item_type }))}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Controller
                    name="item_category_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        {...itemCategoryProps}
                        required
                        options={item_categories}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        onChange={(newOption) => {
                          field.onChange(newOption?.id);
                        }}
                        value={item_categories.find((item_category) => item_category.id == field.value) || null}
                        errorMessage={errors.item_category_id?.message}
                      />
                    )}
                  />
                  <Controller
                    name="code"
                    control={control}
                    render={({ field }) => (
                      <FieldGroup label="Code" required errorMessage={errors.code?.message}>
                        <IMaskInput
                          value={field.value}
                          unmask={false}
                          inputRef={field.ref}
                          onAccept={(value) => {
                            field.onChange(value);
                          }}
                          className="field text-uppercase"
                          {...codeProps}
                        />
                      </FieldGroup>
                    )}
                  />
                  <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} placeholder="Labor" maxLength={100} />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default ItemSubcategoryForm;
